import React from "react";
import {
  toWSDateString,
  WSEmptyState,
  WSTable,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { useHistory } from "react-router-dom";
import type { planEnrollmentApiSpec } from "@wingspanhq/wingspan-api-types";
import { WSQueries } from "../../../query/WSQuery";
import { useQueryActiveMeridioPlanEnrollments } from "../../../query/benefits/useQueryActiveMeridioPlanEnrollments";
import { selectorInsuranceCoverageStatusPillByExpirationDate } from "../selectors/selectorInsuranceCoverageStatusPillByExpirationDate";
import { selectorPlanNameByPlanType } from "../selectors/selectorPlanNameByPlanType";
import { routeHelpersPersonalInsurancePolicyDetails } from "../paths";
import { PATH_BENEFITS_MERIDIO_EXPLORE_PLANS } from "../../Benefits/paths";
import { useGetNow } from "../../../shared/hooks/useGetNow";

// updatedAt is returned by the API but not yet in the types package
type IPlanEnrollment = planEnrollmentApiSpec.PlanEnrollment & {
  updatedAt?: Date;
};

export const PersonalInsuranceList: React.FC<{}> = () => {
  const history = useHistory();
  const now = useGetNow();
  const qActiveMeridioEnrollments = useQueryActiveMeridioPlanEnrollments();
  const goToPersonalInsuranceDetails =
    routeHelpersPersonalInsurancePolicyDetails.useGoTo();

  const columns: Array<WSTableColumn<IPlanEnrollment>> = [
    {
      config: {
        header: {
          text: "Policy name"
        }
      },
      renderFunction: row => (
        <WSTableCell
          text={row.data.name}
          secondaryText={
            row.data.updatedAt
              ? `Updated ${toWSDateString(row.data.updatedAt, "input")}`
              : undefined
          }
        />
      )
    },
    {
      config: {
        header: {
          text: "Type"
        }
      },
      renderFunction: row => (
        <WSTableCell text={selectorPlanNameByPlanType(row.data.planType)} />
      )
    },
    {
      config: {
        header: {
          text: "Status"
        }
      },
      renderFunction: row => {
        const secondaryLines = [
          `Effective on ${toWSDateString(row.data.effectiveDate, "monthDayYear")}`
        ];
        if (row.data.endDate) {
          secondaryLines.push(
            `Ending on ${toWSDateString(row.data.endDate, "monthDayYear")}`
          );
        }
        return (
          <WSTableCell
            pill={selectorInsuranceCoverageStatusPillByExpirationDate(
              row.data.endDate
            )}
            secondaryText={secondaryLines.join("\n")}
          />
        );
      }
    }
  ];

  const tableData = (qActiveMeridioEnrollments.data ?? []).map(item => ({
    data: item,
    id: item.planEnrollmentId
  }));

  return (
    <WSQueries queries={{ qActiveMeridioEnrollments }}>
      {() =>
        tableData.length > 0 ? (
          <WSTable<IPlanEnrollment>
            columns={columns}
            tableData={tableData}
            onRowClick={row => {
              goToPersonalInsuranceDetails({ planEnrollmentId: row.id });
            }}
          />
        ) : (
          <WSEmptyState
            theme="neutral"
            type="tasks"
            title="No policies active"
            description="Once a policy is added and active, it will appear here."
            size="S"
            orientation="horizontal"
            actions={{
              buttons: [
                {
                  kind: "Link",
                  label: "Learn more",
                  rightIcon: "chevron-right",
                  onClick: () =>
                    history.push(PATH_BENEFITS_MERIDIO_EXPLORE_PLANS)
                }
              ]
            }}
          />
        )
      }
    </WSQueries>
  );
};
